/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  CustomTagsField,
  Details,
  type DetailsProps,
  Divider,
  type FormActionData,
  formatDateTime,
  GenericField,
  getFormDiff,
  ImgElement,
  InfoPanel,
  type Nullable,
  type ObjectSchemaDefinition,
  Paragraph,
  ReadOnlyField,
  Section,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { Field, type FormikValues, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { validateOriginPattern } from 'util/validationUtil';
import * as Yup from 'yup';
import {
  EnvironmentServiceState,
  IdentityProvider,
  type IdServiceConfigDataQuery,
  type IdServiceConfiguration,
  type MutationUpdateIdServiceConfigurationArgs,
  type TokenSigningKey,
  type TokenSigningKeyQuery,
  useIdpConfigDataQuery,
  useIdServiceConfigDataQuery,
  useRevokeAndRotateSigningKeyMutation,
  useTokenSigningKeyQuery,
  useUpdateIdServiceConfigMutation,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import {
  formatEnumValueToTitleCase,
  getManagedServiceData,
  getServiceStateIcon,
} from '../../../../util/managedServiceUtil';
import classes from './IdentityServiceConfiguration.module.scss';

type FormData = Nullable<
  MutationUpdateIdServiceConfigurationArgs['input']['patch']
>;

interface IdpData {
  idpId: string;
  idpName: string;
  idpStatus: string;
}

const idServiceConfigSchema = Yup.object<ObjectSchemaDefinition<FormData>>({
  managementSuperAdmins: Yup.array().of(
    Yup.string().email(
      'Management Super Admins must include valid email addresses',
    ),
  ),
  allowedOrigins: Yup.array()
    .of(
      Yup.string()
        .max(255)
        .test(
          'allowed-origins-url',
          'Allowed Origins must be a valid origin starting with http:// or https:// (e.g. https://example.com, https://*.example.com, http://localhost:10053), or * to allow all origins. URL paths and query strings are not allowed for allowed origins.',
          validateOriginPattern,
        ),
    )
    .test(
      'wildcard-exclusive',
      'Wildcard (*) cannot be combined with specific origins. Remove the wildcard or remove all other entries.',
      (value) => {
        if (!value || value.length <= 1) {
          return true;
        }
        return !value.some((v) => v?.trim() === '*');
      },
    ),
  accessTokenLifetime: Yup.number()
    .label('Access Token Lifetime')
    .min(30)
    .required(),
  cookieLifetime: Yup.number().label('Cookie Lifetime').required(),
});

export const IdentityServiceConfiguration: React.FC = () => {
  const apolloClient = useApolloClient();
  const { envId } = useParams<{
    envId: string;
    action: string;
  }>();

  const [serviceState, setServiceState] = useState<
    EnvironmentServiceState | undefined
  >();

  const idServiceConfig = useIdServiceConfigDataQuery({
    client: apolloClient,
    fetchPolicy: 'no-cache',
    variables: {
      environmentId: envId,
    },
  });

  const idpConfig = useIdpConfigDataQuery({
    client: apolloClient,
    fetchPolicy: 'no-cache',
    variables: {
      condition: {
        environmentId: envId,
      },
    },
  });

  const tokenSigningKey = useTokenSigningKeyQuery({
    client: apolloClient,
    fetchPolicy: 'no-cache',
    variables: {
      environmentId: envId,
    },
  });

  const [updateIdServiceConfig] = useUpdateIdServiceConfigMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  // Identity providers
  const [idpInfo, setIdpInfo] = useState<IdpData[]>([]);

  useEffect(() => {
    const info: IdpData[] = [];

    (async () => {
      // Get the service data
      const serviceData = await getManagedServiceData(
        envId,
        ManagedServiceId.ID_SERVICE,
      );
      setServiceState(serviceData?.state);
    })();

    const addIdpInfo = (
      idpId: string,
      idpName: string,
      idpEnabled?: boolean,
    ): void => {
      let idpStatus: string;
      if (idpEnabled === true) {
        idpStatus = 'Enabled';
      } else if (idpEnabled === false) {
        idpStatus = 'Disabled';
      } else {
        idpStatus = 'Not Configured';
      }

      info.push({
        idpId,
        idpName: `${idpName} IDP`,
        idpStatus,
      });
    };

    const axinomIdpEnabled =
      idpConfig.data?.idpConfigurations?.nodes.find(
        (idp) => idp.idpId === IdentityProvider.Axinom,
      )?.enabled ?? false;
    const googleIdpEnabled = idpConfig.data?.idpConfigurations?.nodes.find(
      (idp) => idp.idpId === IdentityProvider.Google,
    )?.enabled;
    const axAuthIdpEnabled = idpConfig.data?.idpConfigurations?.nodes.find(
      (idp) => idp.idpId === IdentityProvider.AxAuth,
    )?.enabled;
    const azureAdIdpEnabled = idpConfig.data?.idpConfigurations?.nodes.find(
      (idp) => idp.idpId === IdentityProvider.AzureAd,
    )?.enabled;

    addIdpInfo('AX_AUTH', 'AxAuth', axAuthIdpEnabled);
    addIdpInfo('AZURE_AD', 'Azure AD', azureAdIdpEnabled);
    addIdpInfo('GOOGLE', 'Google', googleIdpEnabled);
    addIdpInfo('AXINOM', 'Axinom', axinomIdpEnabled);

    setIdpInfo(info);
  }, [idpConfig, apolloClient, envId, serviceState]);

  const { actions, updatedData } = useActions(envId);

  if (tokenSigningKey.data?.tokenSigningKey && updatedData) {
    Object.assign(tokenSigningKey.data?.tokenSigningKey, updatedData);
  }

  const onSubmit = useCallback(
    async (
      formData: FormData,
      initialData: DetailsProps<FormData>['initialData'],
    ): Promise<void> => {
      if (initialData.loading) {
        return;
      }

      await updateIdServiceConfig({
        variables: {
          input: {
            environmentId: envId,
            patch: createUpdateDto(formData, initialData.data),
          },
        },
      });
    },
    [updateIdServiceConfig, envId],
  );

  return (
    <Details
      defaultTitle="Identity Service"
      subtitle="Configuration"
      actions={
        idServiceConfig.data?.idServiceConfigurationByEnvironmentId
          ? actions
          : []
      }
      validationSchema={idServiceConfigSchema}
      initialData={{
        data: idServiceConfig.data?.idServiceConfigurationByEnvironmentId || {},
        loading: idServiceConfig.loading,
        error: idServiceConfig.error?.message,
      }}
      saveData={onSubmit}
      infoPanel={
        <Panel
          idServiceConfig={
            idServiceConfig.data?.idServiceConfigurationByEnvironmentId
          }
          idpInfo={idpInfo}
        />
      }
    >
      <Form
        idServiceConfig={
          idServiceConfig.data?.idServiceConfigurationByEnvironmentId
        }
        tokenSigningKey={tokenSigningKey.data?.tokenSigningKey}
        serviceState={serviceState}
      />
    </Details>
  );
};

const Panel: React.FC<{
  idServiceConfig:
    | IdServiceConfigDataQuery['idServiceConfigurationByEnvironmentId']
    | undefined;
  idpInfo: IdpData[];
}> = ({ idServiceConfig, idpInfo }) => {
  const { values } = useFormikContext<IdServiceConfiguration>();

  return (
    <>
      {idServiceConfig && (
        <InfoPanel>
          <Section>
            <Paragraph title="Last Modified">
              {formatDateTime(values.updatedAt)} by {values.updatedBy}
            </Paragraph>
          </Section>
          <Section title="Identity Providers">
            {idpInfo.map((idpData) => (
              <Paragraph key={idpData.idpName} title={idpData.idpName}>
                {idpData.idpStatus}
              </Paragraph>
            ))}
          </Section>
          <Section>
            <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
            <Paragraph title="Environment ID">{values.environmentId}</Paragraph>
          </Section>
        </InfoPanel>
      )}
    </>
  );
};

const allowedOriginsTooltip = (
  <span>
    Origins allowed to initiate login flows. Management System origins are
    always included automatically. Use this field to allow any additional
    origins such as localhost development URLs. It also supports wildcard
    patterns with the &quot;*&quot; character (valid examples:
    https://your-domain.com, https://*.your-domain.com). See{' '}
    <a href="https://docs.axinom.com/concepts/management-system/#accessing-the-management-system">
      Mosaic Documentation
    </a>{' '}
    for typical local development URLs.
  </span>
);

const Form: React.FC<{
  idServiceConfig:
    | IdServiceConfigDataQuery['idServiceConfigurationByEnvironmentId']
    | undefined;
  tokenSigningKey: TokenSigningKeyQuery['tokenSigningKey'] | undefined;
  serviceState: EnvironmentServiceState | undefined;
}> = ({ idServiceConfig, tokenSigningKey, serviceState }) => {
  return (
    <>
      <GenericField name="serviceState" label="Status">
        <div className={classes.status_container}>
          <ImgElement
            src={getServiceStateIcon(serviceState, ManagedServiceId.ID_SERVICE)}
            style={{ width: '20px', height: '20px' }}
            decorative={true}
          />
          <div className={classes.status}>
            {formatEnumValueToTitleCase(serviceState)}
          </div>
        </div>
      </GenericField>

      <Divider />

      {idServiceConfig && (
        <>
          <Field
            name="managementSuperAdmins"
            label="Management Super Admins (email)"
            as={CustomTagsField}
          />
          <Field
            name="allowedOrigins"
            label="Allowed Origins"
            tooltipContent={allowedOriginsTooltip}
            as={CustomTagsField}
          />
          <Field
            name="accessTokenLifetime"
            label="Access Token Lifetime (seconds)"
            type="number"
            autoComplete="off"
            as={SingleLineTextField}
          />
          <Field
            name="cookieLifetime"
            label="Cookie Lifetime (seconds)"
            type="number"
            autoComplete="off"
            as={SingleLineTextField}
          />
          <Field
            name="serviceAccountTokenLifetime"
            label="Service Account Token Lifetime (seconds)"
            type="number"
            autoComplete="off"
            as={SingleLineTextField}
          />
          <Divider />
          <Field
            name="currentTokenSigningKeyId"
            label="Current Token Signing Key ID"
            value={tokenSigningKey?.kid}
            as={ReadOnlyField}
          />
          <Field
            name="tokenSigningKeyNextRotation"
            label="Token Signing Key Next Rotation"
            value={tokenSigningKey?.nextRotationAt}
            transform={formatDateTime}
            as={ReadOnlyField}
          />
        </>
      )}
    </>
  );
};

function useActions(id: string): {
  readonly actions: FormActionData<FormData>[];
  readonly updatedData?: TokenSigningKey;
} {
  const apolloClient = useApolloClient();
  const [revokeAndRotateSigningKey] = useRevokeAndRotateSigningKeyMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });
  const [updatedData, setUpdatedData] = useState<TokenSigningKey>();

  const actions: FormActionData<FormData>[] = [];

  actions.push(
    {
      label: 'Identity Providers',
      path: `/env/${id}/services/${ManagedServiceId.ID_SERVICE}/config/identityProviders`,
    },
    {
      label: 'Email Whitelist Patterns',
      path: `/env/${id}/services/${ManagedServiceId.ID_SERVICE}/config/emailWhitelistPatterns`,
    },
    {
      label: 'Revoke & Rotate Signing Key',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'Once a new signing key is generated, existing users will need to refresh their access tokens by refreshing the browser or logging in again.',
      },
      onActionSelected: async () => {
        const result = await revokeAndRotateSigningKey({
          variables: {
            envId: id,
          },
        });

        setUpdatedData(
          result.data?.revokeAndRotateSigningKey
            ?.tokenSigningKey as TokenSigningKey,
        );
      },
    },
  );

  return { actions, updatedData } as const;
}

function createUpdateDto(
  currentValues: FormikValues,
  initialValues: FormikValues | undefined | null,
): FormikValues {
  const updateData = getFormDiff(currentValues, initialValues);
  delete updateData['id'];
  return updateData;
}

export const IdentityServiceConfigurationCrumb: BreadcrumbResolver = () =>
  'Identity Service';
